import React, { useState } from "react";
import { Modal, ModalHeader, ModalBody, Button, Spinner, Alert } from "reactstrap";
import ReportFrame from "./ReportFrame";

// ---------------------------------------------------------------------------
// src/pages/KPI/ReportViewerModal.js
//
// In-hub viewer for embeddable Power BI reports. Opens the selected report's
// publish-to-web URL inside a safe <ReportFrame>, with:
//   • Fullscreen toggle (reactstrap Modal `fullscreen`).
//   • Open in new tab.
//   • Graceful blocked-embed fallback (handled inside ReportFrame).
//
// Controlled by the caller: pass the `report` to show (or null) + onClose.
// Pure presentation.
//
// Props
//   report     the report object from reportConfig (or null when closed)
//   url        the report's embed link, fetched by the hub from
//              GET /api/reports/embed-urls (Go-live parity 2026-09-24: the
//              publish-to-web links are no longer in the JS bundle)
//   urlStatus  "loading" | "ready" | "error" — state of that fetch
//   onClose    () => void
// ---------------------------------------------------------------------------

const ReportViewerModal = ({ report, url, urlStatus, onClose }) => {
    const [fullscreen, setFullscreen] = useState(false);
    const isOpen = !!report;

    const openNewTab = () => {
        if (url) window.open(url, "_blank", "noopener,noreferrer");
    };

    // Reset fullscreen when the modal closes so the next open starts windowed.
    const handleClose = () => {
        setFullscreen(false);
        onClose?.();
    };

    return (
        <Modal
            isOpen={isOpen}
            toggle={handleClose}
            size="xl"
            fullscreen={fullscreen ? true : undefined}
            backdrop="static"
            scrollable
        >
            <ModalHeader toggle={handleClose} className="align-items-center">
                <span className="d-inline-flex align-items-center">
                    <i className={`${report?.icon || "ri-file-chart-line"} me-2 text-primary`} />
                    {report?.title}
                </span>
            </ModalHeader>
            <ModalBody style={{ height: fullscreen ? "calc(100vh - 120px)" : "75vh", padding: 0 }}>
                {report && (url ? (
                    <ReportFrame
                        src={url}
                        title={report.title}
                        onOpenNewTab={openNewTab}
                    />
                ) : urlStatus === "loading" ? (
                    <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
                        <Spinner color="primary" />
                        <span className="mt-2 fs-13">Loading report…</span>
                    </div>
                ) : (
                    <Alert color="warning" className="m-3">
                        <i className="ri-lock-line me-1" />
                        This report's link could not be loaded, or you do not have access to it.
                    </Alert>
                ))}
            </ModalBody>
            <div className="d-flex justify-content-end gap-2 p-2 border-top">
                <Button color="light" onClick={() => setFullscreen((v) => !v)}>
                    <i className={`me-1 ${fullscreen ? "ri-fullscreen-exit-line" : "ri-fullscreen-line"}`} />
                    {fullscreen ? "Exit fullscreen" : "Fullscreen"}
                </Button>
                <Button color="primary" outline onClick={openNewTab} disabled={!url}>
                    <i className="ri-external-link-line me-1" />
                    Open in new tab
                </Button>
                <Button color="secondary" onClick={handleClose}>
                    Close
                </Button>
            </div>
        </Modal>
    );
};

export default ReportViewerModal;
